import type { ReportColumn, ReportColumnFormat, ResultCell } from "../../../api/portalReportsApi";
import { fmtBytes, fmtDateTime, fmtDuration, fmtTime } from "../ReportBits";

const pad2 = (n: number) => String(n).padStart(2, "0");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Numeric value of a cell, or null when it isn't a number. */
export function cellNumber(v: ResultCell): number | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "boolean") return v ? 1 : 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtNumber(n: number, decimals: number): string {
  return n.toLocaleString("en-GB", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** Formats a value with a column format (no time zone shift when `isLocalTime`). */
export function formatValue(v: ResultCell, format: ReportColumnFormat | undefined, decimals?: number, isLocalTime?: boolean): string {
  if (v == null || v === "") return "";
  switch (format) {
    case "integer": {
      const n = cellNumber(v);
      return n == null ? String(v) : fmtNumber(Math.round(n), 0);
    }
    case "decimal": {
      const n = cellNumber(v);
      return n == null ? String(v) : fmtNumber(n, decimals ?? 2);
    }
    case "currency": {
      const n = cellNumber(v);
      return n == null ? String(v) : fmtNumber(n, decimals ?? 2);
    }
    case "percent": {
      const n = cellNumber(v);
      return n == null ? String(v) : `${fmtNumber(n, decimals ?? 1)}%`;
    }
    case "bool": {
      if (typeof v === "boolean") return v ? "Yes" : "No";
      const s = String(v).toLowerCase();
      return s === "1" || s === "true" ? "Yes" : "No";
    }
    case "durationMmSs": {
      const n = cellNumber(v);
      return n == null ? String(v) : fmtDuration(Math.round(n));
    }
    case "durationHhMmSs": {
      const n = cellNumber(v);
      if (n == null) return String(v);
      const s = Math.max(0, Math.round(n));
      return `${pad2(Math.floor(s / 3600))}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;
    }
    case "bytes": {
      const n = cellNumber(v);
      return n == null ? String(v) : fmtBytes(n);
    }
    case "date":
      return fmtLocalOrUtc(String(v), "date", isLocalTime);
    case "datetime":
      return fmtLocalOrUtc(String(v), "datetime", isLocalTime);
    case "time":
      return fmtLocalOrUtc(String(v), "time", isLocalTime);
    default:
      return typeof v === "number" ? fmtNumber(v, Number.isInteger(v) ? 0 : 2) : String(v);
  }
}

/** UTC values go through the report time zone formatters; local ones are shown as sent. */
function fmtLocalOrUtc(s: string, kind: "date" | "datetime" | "time", isLocalTime?: boolean): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (!m) return s;
  if (isLocalTime || kind === "date" && !m[4]) {
    const day = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
    if (kind === "date") return day;
    const time = `${m[4] ?? "00"}:${m[5] ?? "00"}:${m[6] ?? "00"}`;
    return kind === "time" ? time : `${day} ${time}`;
  }
  if (kind === "time") return fmtTime(s);
  const full = fmtDateTime(s);
  return kind === "date" ? full.replace(/ \d{2}:\d{2}:\d{2}$/, "") : full;
}

export function formatCell(v: ResultCell, col: ReportColumn | undefined): string {
  return formatValue(v, col?.format, col?.decimals, col?.isLocalTime);
}

const NUMERIC_FORMATS: ReportColumnFormat[] = ["integer", "decimal", "currency", "percent", "durationMmSs", "durationHhMmSs", "bytes"];

export function isNumericFormat(format: ReportColumnFormat | undefined): boolean {
  return !!format && NUMERIC_FORMATS.includes(format);
}

/** Default alignment: numbers right, the rest left. */
export function columnAlign(col: ReportColumn): "left" | "right" | "center" {
  if (col.align) return col.align;
  return isNumericFormat(col.format) ? "right" : "left";
}

/** Guesses a display format from the SQL type of a described/observed column. */
export function guessFormat(sqlType: string | undefined | null, name?: string): ReportColumnFormat {
  const t = (sqlType ?? "").toLowerCase();
  const n = (name ?? "").toLowerCase();
  if (t.startsWith("bit")) return "bool";
  if (/^(tinyint|smallint|int|bigint)/.test(t)) {
    if (/duration|seconds|secs/.test(n)) return "durationMmSs";
    return "integer";
  }
  if (/^(decimal|numeric|money|smallmoney|float|real)/.test(t)) return /amount|price|cost|fee|rate/.test(n) ? "currency" : "decimal";
  if (t.startsWith("date") && !t.startsWith("datetime")) return "date";
  if (t.startsWith("datetime") || t.startsWith("smalldatetime")) return "datetime";
  if (t.startsWith("time")) return "time";
  return "text";
}

/** Aggregates a column over rows for the totals row. */
export function columnTotal(values: ResultCell[], total: ReportColumn["total"]): number | null {
  if (!total || total === "none") return null;
  if (total === "count") return values.filter((v) => v != null && v !== "").length;
  const nums = values.map(cellNumber).filter((n): n is number => n != null);
  if (nums.length === 0) return null;
  switch (total) {
    case "sum":
      return nums.reduce((a, b) => a + b, 0);
    case "avg":
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case "min":
      return Math.min(...nums);
    case "max":
      return Math.max(...nums);
    default:
      return null;
  }
}
